<template>
  <div class="scene-view">
    <div
      id="engineContainer"
      class="engine-container"
      ref="engineContainer"
      :style="`visibility:${canvasVisible ? 'visible' : 'hidden'}`" />
    <EngineLoading
      v-if="isLoadingVisible"
      :percentage="percentage" />
  </div>
</template>

<script>
import EngineLoading from '@/components/Loading.vue'
import { Store } from '@/utils/store'

import { isTemplateVersionConsistentWithEngine } from '@/utils/generalMethods'

// 需要设置坐标系和基点的数据类型
const SETCRS_DATA_TYPE = [0, 11, 15]

export default {
  data() {
    return {
      percentage: 0,
      isLoadingVisible: true,
      canvasVisible: false,

      // 分批加载的数据集
      batchedDataSets: [],
      isStartLoadDataSet: false
    }
  },
  components: {
    EngineLoading
  },
  mounted() {
    this.addEventListener()
  },
  beforeDestroy() {
    this.unloadAllMainSce()
    this.removeEventListener()
    this.isLoadingVisible = false

    window.removeEventListener('beforeunload', this.onBeforeUnload)
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    addEventListener() {
      document.addEventListener('REDataSetLoadProgress', this.onProgress)
      document.addEventListener('RESystemEngineCreated', this.onCreated)
      document.addEventListener('REDataSetLoadFinish', this.onFinish)
      document.addEventListener('RESystemUIEvent', this.onUIClick)
      document.addEventListener('RESystemSelElement', this.onSelElement)
      document.addEventListener('RESystemSelShpElement', this.onSelShpElement)
      document.addEventListener('mousedown', this.onMouseDown)

      window.addEventListener('beforeunload', this.onBeforeUnload)
      window.addEventListener('resize', this.onResize)
    },
    removeEventListener() {
      document.removeEventListener('REDataSetLoadProgress', this.onProgress)
      document.removeEventListener('RESystemEngineCreated', this.onCreated)
      document.removeEventListener('REDataSetLoadFinish', this.onFinish)
      document.removeEventListener('RESystemUIEvent', this.onUIClick)
      document.removeEventListener('RESystemSelElement', this.onSelElement)
      // prettier-ignore
      document.removeEventListener('RESystemSelShpElement', this.onSelShpElement)
      document.removeEventListener('mousedown', this.onMouseDown)
    },
    initEngine() {
      window.BlackHole3D = window.BlackHole3D ? window.BlackHole3D : {}
      window.BlackHole3D.canvas = document.getElementById('engineCanvas')

      if (typeof window.BlackHole3D.RealBIMWeb === 'undefined') {
        window.BlackHole3D = window.CreateBlackHoleWebSDK(window.BlackHole3D)
      }
    },
    onCreated() {
      const timer = setInterval(() => {
        if (this.isEnginePreLoaded()) {
          clearInterval(timer)
          this.copyCanvas()

          this.setSceneViewMode()
          this.$eventManage.fire('engineCreated', {})
        }
      }, 500)
    },
    onProgress(e) {
      if (!this.isStartLoadDataSet) return

      let percentage = e.detail.progress

      if (percentage < 10) {
        this.fromZeroFlag = true
      }
      if (percentage === 12 && this.fromZeroFlag) {
        percentage = 10
      }

      this.percentage = percentage
    },
    onFinish(e) {
      const BlackHole3D = window.BlackHole3D
      BlackHole3D.Graphics.setSysUIPanelVisible(false)

      if (!this.isStartLoadDataSet) return

      this.canvasVisible = true
      this.isLoadingVisible = false

      if (this.batchedDataSets.length) {
        const dataSets = this.batchedDataSets.pop()
        this.loadDataSets(dataSets, false)
      } else {
        // 解决场景中存在二维图纸，矢量数据时，相机定位不准的问题
        BlackHole3D.Camera.setCamBelowTerrain(true)
        this.$eventManage.fire('dataSetLoadFinish', {})

        if (window.isGridClipEnable) {
          BlackHole3D.Grid.setClipEnable(true)
        }

        // 解决模型加载完成后，键盘WASD不能控制引擎相机移动的问题
        // prettier-ignore
        setTimeout(() => { BlackHole3D.canvas.focus(); }, 500);
      }
    },
    incrementalLoadDataSets(dataSets, count) {
      const BlackHole3D = window.BlackHole3D
      BlackHole3D.Graphics.setSysUIPanelVisible(false)

      for (let i = 0; i < dataSets.length; i += count) {
        const batchedDataSets = dataSets.slice(i, i + count)
        this.batchedDataSets.push(batchedDataSets)
      }

      const firstDataSets = this.batchedDataSets.pop()
      // 对于第一批加载的数据集, isClearLoaded需要设置为true, 否则会导致REDataSetLoadFinish事件不会触发
      this.loadDataSets(firstDataSets, true)
    },
    async loadDataSets(dataSets, isClearLoaded) {
      this.isStartLoadDataSet = true

      const isVersionConsist = await isTemplateVersionConsistentWithEngine()
      const BlackHole3D = window.BlackHole3D
      if (!isVersionConsist) {
        const dataSetList = [
          {
            dataSetId: 'res_skybox',
            resourcesAddress: window.skyBoxUrl
          }
        ]
        BlackHole3D.Model.loadDataSet(dataSetList)

        BlackHole3D.Common.setMaxResMemMB(2000)
        BlackHole3D.Common.setExpectMaxInstMemMB(1900)
        BlackHole3D.Common.setExpectMaxInstDrawFaceNum(10000000)
        BlackHole3D.Common.setPageLoadLev(2)
        this.$message.error('请升级SDK版本至3.2.0.3247之后的版本')
        return
      }

      let dataSetList = []
      if (!dataSets.length) {
        let obj = {
          dataSetId: 'res_skybox',
          resourcesAddress: window.skyBoxUrl
        }
        dataSetList.push(obj)
      } else {
        dataSets.forEach(item => {
          let obj = {
            dataSetId: item.resId,
            resourcesAddress: this.getResourcesAddress(item),
            dataSetCRS: this.getDataSetCRS(item),
            engineOrigin: this.getEngineOrigin(item),
            dataSetCRSNorth: this.getDataSetCRSNorth(item),
            useTransInfo: true,
            transInfo: this.getTransInfo(item),
            dataSetSGContent: item.context ? item.context : ''
          }
          if (item.dataSetType === 21 || item.dataSetType === 22) {
            obj.groundDisplay = item.isTouchDown
            obj.terrSuffix = item.isTouchDown ? '' : item.resId
          }

          dataSetList.push(obj)
        })
      }
      BlackHole3D.Model.loadDataSet(dataSetList, isClearLoaded)

      BlackHole3D.Common.setMaxResMemMB(2000)
      BlackHole3D.Common.setExpectMaxInstMemMB(1900)
      BlackHole3D.Common.setExpectMaxInstDrawFaceNum(10000000)
      BlackHole3D.Common.setPageLoadLev(2)
    },
    onUIClick(event) {
      let detail = event.detail
      let btnName = detail.btnname
      if (btnName.includes('Measure')) {
        window.BlackHole3D.BIM.delAllSelElems()
      }

      if (btnName.includes('UIClr_Style')) {
        this.$eventManage.fire('themeChange', { themeName: btnName })
      }

      if (btnName.includes('Catalog_Tree')) {
        let btnState = event.detail.btnstate
        if (btnState == 0) {
          window.BlackHole3D.Graphics.setBtnActiveState(btnName, 1)
        } else {
          window.BlackHole3D.Graphics.setBtnActiveState(btnName, 0)
        }
        this.$eventManage.fire('customBtnClick', { btnName })
      }

      if (btnName.includes('Attribute')) {
        let btnState = event.detail.btnstate
        if (btnState == 0) {
          window.BlackHole3D.Graphics.setBtnActiveState(btnName, 1)
        } else {
          window.BlackHole3D.Graphics.setBtnActiveState(btnName, 0)
        }
        this.$eventManage.fire('customBtnClick', { btnName })
      }

      if (btnName.includes('Explode')) {
        let btnState = event.detail.btnstate
        if (btnState == 0) {
          window.BlackHole3D.Graphics.setBtnActiveState(btnName, 1)
        } else {
          window.BlackHole3D.Graphics.setBtnActiveState(btnName, 0)
        }
        this.$eventManage.fire('customBtnClick', { btnName })
      }
    },
    onSelElement() {
      this.setSelElemsBlendAttr()

      let probeInfo = window.BlackHole3D.Probe.getCurCombProbeRet()
      let elemInfo = {
        dataSetId: probeInfo.dataSetId,
        elemId: probeInfo.elemId,
        elemType: probeInfo.elemType
      }
      this.$eventManage.fire('selElement', { elemInfo })
    },
    onSelShpElement() {
      const BlackHole3D = window.BlackHole3D
      let probeInfo = BlackHole3D.Probe.getCurCombProbeRet()

      let elemType = probeInfo.elemType
      if (elemType !== 'ShapeElem') return

      let shpElemId = probeInfo.elemId
      let dataSetId = ''
      if (shpElemId && typeof shpElemId === 'string') {
        dataSetId = shpElemId && shpElemId.split('_')[1]
      }
      let shpElemInfo = {
        dataSetId: dataSetId,
        elemId: probeInfo.elemId,
        elemType: probeInfo.elemType
      }
      this.$eventManage.fire('vectorClick', { shpElemInfo })
    },
    onMouseDown(e) {
      // 按下鼠标中键时，禁用滚动
      if (e.button === 1) {
        e.preventDefault()
      }
    },
    onBeforeUnload() {
      window.BlackHole3D.releaseEngine()
    },
    onResize() {
      this.setCanvasSize()
    },
    getResourcesAddress(dataSetInfo) {
      let resourcesAddress = ''
      if (window.isMinIO) {
        resourcesAddress = `${dataSetInfo.resourcesAddress}${dataSetInfo.resId}`
      } else {
        resourcesAddress = dataSetInfo.resourcesAddress
      }
      return resourcesAddress
    },
    getDataSetCRS(dataSetInfo) {
      if (dataSetInfo.readonlyCoordinate) return null

      if (!SETCRS_DATA_TYPE.includes(dataSetInfo.dataSetType)) return null

      let crsConfig = dataSetInfo.coordinatesConfig
      if (crsConfig.coordinatesType === 'None') return null

      if (crsConfig.coordinatesType === 'CalibrationPoint') {
        let crsPoint = crsConfig.coordinatesPoint
        let crs = `ENU:${crsPoint.latitude},${crsPoint.longitude}`
        return crs
      } else {
        return crsConfig.coordinates
      }
    },
    getEngineOrigin(dataSetInfo) {
      if (dataSetInfo.readonlyCoordinate) return null

      if (!SETCRS_DATA_TYPE.includes(dataSetInfo.dataSetType)) return null

      let engineOrigin = []
      let crsConfig = dataSetInfo.coordinatesConfig

      let origin = crsConfig.basePoint
      let originArray = []
      if (origin) {
        originArray = origin.split(',').map(Number)
      }

      if (crsConfig.coordinatesType === 'CalibrationPoint') {
        let crsPoint = crsConfig.coordinatesPoint
        let point = crsPoint.coordinates
        let pointArray = []
        if (point) {
          pointArray = point.split(',').map(Number)
        } else {
          pointArray = [0, 0, 0]
        }

        if (originArray.length) {
          originArray.forEach((item, index) => {
            let result = item - pointArray[index]
            engineOrigin.push(result)
          })
        } else {
          engineOrigin = pointArray
        }
      } else {
        engineOrigin = originArray
      }

      if (engineOrigin.length) {
        return engineOrigin
      } else {
        return null
      }
    },
    getDataSetCRSNorth(dataSetInfo) {
      if (dataSetInfo.readonlyCoordinate) return null

      if (!SETCRS_DATA_TYPE.includes(dataSetInfo.dataSetType)) return null
      let crsConfig = dataSetInfo.coordinatesConfig
      if (crsConfig.northAngle) {
        return Number(crsConfig.northAngle)
      } else {
        return null
      }
    },
    getTransInfo(dataSetInfo) {
      let sacle = dataSetInfo.scale.split(' ').map(Number)
      let rotate = dataSetInfo.rotate.split(' ').map(Number)
      let translation = dataSetInfo.translation.split(' ').map(Number)
      let transInfo = [sacle, rotate, translation]
      return transInfo
    },
    focusDataSets(dataSets, isClearSel) {
      const BlackHole3D = window.BlackHole3D
      if (isClearSel) {
        BlackHole3D.BIM.delAllSelElems()
      }

      const terrainType = [10, 13, 21, 22]
      if (dataSets.length === 1) {
        let dataSetInfo = dataSets[0]
        let dataSetId = dataSetInfo.dataSetId
        let locType = BlackHole3D.RECamDirEm.CAM_DIR_DEFAULT
        if (terrainType.includes(dataSetInfo.dataSetType)) {
          locType = BlackHole3D.RECamDirEm.CAM_DIR_TOP
        } else {
          locType = BlackHole3D.RECamDirEm.CAM_DIR_TOPRIGHTBACK
        }

        BlackHole3D.Camera.setCamLocateToDataSet(dataSetId, 1.0, locType)
      } else {
        let bbox = this.getTotalBBox(dataSets)
        let isIncludesTerrainType = dataSets.some(el =>
          terrainType.includes(el.dataSetType)
        )
        let locType = BlackHole3D.RECamDirEm.CAM_DIR_DEFAULT
        if (isIncludesTerrainType) {
          locType = BlackHole3D.RECamDirEm.CAM_DIR_TOP
        } else {
          locType = BlackHole3D.RECamDirEm.CAM_DIR_TOPRIGHTBACK
        }
        BlackHole3D.Camera.setCamLocateToBound(bbox, 1.0, locType)
      }
    },
    focusElems(dataSets, isFocus, isHighLight) {
      const BlackHole3D = window.BlackHole3D
      BlackHole3D.BIM.delAllSelElems()

      if (isFocus) {
        let locIDList = []
        dataSets.forEach(item => {
          let obj = {
            dataSetId: item.dataSetId,
            elemIdList: item.elemIds
          }
          locIDList.push(obj)
        })
        let locType = BlackHole3D.RECamDirEm.CAM_DIR_TOPRIGHTBACK
        BlackHole3D.Camera.setCamLocateToElem(locIDList, 1.0, locType)
      }

      if (isHighLight) {
        dataSets.forEach(item => {
          BlackHole3D.BIM.addToSelElems(item.dataSetId, item.elemIds)
        })
        let color = new BlackHole3D.REColor(254, 64, 7, 180)
        BlackHole3D.BIM.setSelElemsAttr(color, 1, true)
      }
    },
    showDataSets(dataSets) {
      const elemDataSetType = [17, 18, 19]

      dataSets.forEach(item => {
        let { dataSetId, dataSetType } = item
        if (elemDataSetType.includes(dataSetType)) {
          let elemId = item.elemId
          this.showElement(dataSetId, [elemId])
        } else {
          if (dataSetType !== 0) {
            let terrId = window.BlackHole3D.Terrain.getDataSetTerrId(dataSetId)
            if (terrId) {
              this.showCadAndVector(dataSetId)
            } else {
              window.BlackHole3D.Grid.setValidState(dataSetId, true)
            }
          } else {
            window.BlackHole3D.BIM.resetElemAttr(dataSetId, [])
          }
        }
      })
    },
    hideDataSets(dataSets) {
      const elemDataSetType = [17, 18, 19]

      dataSets.forEach(item => {
        let { dataSetId, dataSetType } = item
        if (elemDataSetType.includes(dataSetType)) {
          let elemId = item.elemId
          this.hiddenElement(dataSetId, [elemId])
        } else {
          if (dataSetType !== 0) {
            let terrId = window.BlackHole3D.Terrain.getDataSetTerrId(dataSetId)
            if (terrId) {
              this.hideCadAndVector(dataSetId)
            } else {
              window.BlackHole3D.Grid.setValidState(dataSetId, false)
            }
          } else {
            window.BlackHole3D.BIM.setElemAlpha(dataSetId, [], 0)
          }
        }
      })
    },
    showElement(dataSetId, childNodeIds) {
      window.BlackHole3D.BIM.delAllSelElems()
      window.BlackHole3D.BIM.resetElemAttr(dataSetId, childNodeIds)
    },
    hiddenElement(dataSetId, childNodeIds) {
      window.BlackHole3D.BIM.delAllSelElems()
      window.BlackHole3D.BIM.setElemAlpha(dataSetId, childNodeIds, 0)
    },
    getTotalBBox(dataSets) {
      let xValue = []
      let yValue = []
      let zValue = []

      const BlackHole3D = window.BlackHole3D
      dataSets.forEach(item => {
        let bbox
        if (item.dataSetType === 0) {
          bbox = BlackHole3D.BIM.getTotalBV(item.dataSetId)
        } else {
          bbox = BlackHole3D.Grid.getDataSetBV(item.dataSetId)
        }
        xValue.push(bbox[0], bbox[1])
        yValue.push(bbox[2], bbox[3])
        zValue.push(bbox[4], bbox[5])
      })

      let totalBBox = [
        [Math.min(...xValue), Math.min(...yValue), Math.min(...zValue)],
        [Math.max(...xValue), Math.max(...yValue), Math.max(...zValue)]
      ]

      return totalBBox
    },
    hideCadAndVector(dataSetId) {
      const BlackHole3D = window.BlackHole3D
      let unitNames = BlackHole3D.Terrain.getAllUnitNames(dataSetId)
      let unitId = unitNames[0] ? unitNames[0] : ''
      let resType = BlackHole3D.RETerrResEm.ALL
      BlackHole3D.Terrain.setUnitActive(dataSetId, unitId, resType, false)
    },
    showCadAndVector(dataSetId) {
      const BlackHole3D = window.BlackHole3D
      let unitNames = BlackHole3D.Terrain.getAllUnitNames(dataSetId)
      let unitId = unitNames[0] ? unitNames[0] : ''
      let resType = BlackHole3D.RETerrResEm.ALL
      BlackHole3D.Terrain.setUnitActive(dataSetId, unitId, resType, true)
    },
    highLightWaters(waterIds) {
      const Water = window.BlackHole3D.Water
      Water.setCamToData(waterIds)
    },
    highLightExtrudes(extrudeIds) {
      const Extrude = window.BlackHole3D.Extrude
      Extrude.setCamToData(extrudeIds)
    },
    showWaters(waterIds) {
      waterIds.forEach(item => {
        window.BlackHole3D.Water.setVisible(item, true)
      })
    },
    hideWaters(waterIds) {
      waterIds.forEach(item => {
        window.BlackHole3D.Water.setVisible(item, false)
      })
    },
    showExtrudes(extrudeIds) {
      extrudeIds.forEach(item => {
        window.BlackHole3D.Extrude.setVisible(item, true)
      })
    },
    hideExtrudes(extrudeIds) {
      extrudeIds.forEach(item => {
        window.BlackHole3D.Extrude.setVisible(item, false)
      })
    },
    setUnitShpHole(dataSetId, isDigHole) {
      const BlackHole3D = window.BlackHole3D
      let unitNames = BlackHole3D.Terrain.getAllUnitNames(dataSetId)
      let unitId = unitNames[0] ? unitNames[0] : ''
      const resType = BlackHole3D.RETerrResEm.ALL
      const Terrain = BlackHole3D.Terrain
      Terrain.setUnitShpHole(dataSetId, unitId, resType, isDigHole)
    },
    setUnitLayerLevel(dataSetId, level) {
      const BlackHole3D = window.BlackHole3D
      let unitNames = BlackHole3D.Terrain.getAllUnitNames(dataSetId)
      let unitId = unitNames[0] ? unitNames[0] : ''
      let resType = BlackHole3D.RETerrResEm.ALL
      BlackHole3D.Terrain.setUnitLayerlev(dataSetId, unitId, resType, level)
    },
    setUnitLODLevRange(dataSetId, range) {
      const BlackHole3D = window.BlackHole3D
      let unitNames = BlackHole3D.Terrain.getAllUnitNames(dataSetId)
      let unitId = unitNames[0] ? unitNames[0] : ''
      let resType = BlackHole3D.RETerrResEm.ALL
      BlackHole3D.Terrain.setUnitLODLevRange(dataSetId, unitId, resType, range)
    },
    setEngineWorldCRS(worldCRS) {
      window.BlackHole3D.Coordinate.setEngineWorldCRS(worldCRS)
    },
    setViewAngle(camLoc) {
      let forceCamLoc = new window.BlackHole3D.REForceCamLoc()
      forceCamLoc.force = true
      forceCamLoc.camPos = camLoc.camPos
      forceCamLoc.camRotate = camLoc.camRotate
      window.BlackHole3D.Camera.setCamForcedInitLoc(forceCamLoc)
    },
    unloadAllMainSce() {
      window.BlackHole3D.Extrude.delData([])
      window.BlackHole3D.Water.delData([])
      // 卸载所有数据会导致挖洞纹理丢失，并且进入场景编辑和预览的时候会添加纹理，
      // 而引擎中纹理大小有限制，因此离开前需要删除所有纹理
      window.BlackHole3D.Extrude.delAllExtrudeFaceTex()
      Store.extrudeTextureList = []

      window.BlackHole3D.Entity.delEntities('', '', [])
      window.BlackHole3D.Model.unloadAllDataSet()
    },
    setSelElemsBlendAttr() {
      const BlackHole3D = window.BlackHole3D
      let blendAttr = BlackHole3D.BIM.getSelElemsBlendAttr()
      blendAttr.elemClr = new BlackHole3D.REColor(255, 0, 0, 200)
      blendAttr.clrWeight = 128
      blendAttr.alphaWeight = 200
      BlackHole3D.BIM.setSelElemsBlendAttr(blendAttr)
    },
    addCustomBtn(uiID, stateParList, activeStateId) {
      let btnInfo = new window.BlackHole3D.REUIBtnInfo()
      btnInfo.uiID = uiID
      btnInfo.activeStateId = activeStateId

      let tempStateParList = stateParList.map(item => {
        let statePar = new window.BlackHole3D.REUIBtnStateInfo()
        statePar.hintText = item.hintText
        statePar.texPath = item.texPath
        return statePar
      })

      btnInfo.stateParList = tempStateParList
      window.BlackHole3D.Graphics.createSysPanelBtn(btnInfo)
    },
    setSceneViewMode() {
      let viewMode = this.$route.query.viewMode
      if (viewMode === 'Sphere') {
        window.BlackHole3D.Common.setFakeSphMode(true)
      } else {
        window.BlackHole3D.Common.setFakeSphMode(false)
      }
    },
    setCanvasSize() {
      const domRef = this.$refs.engineContainer
      if (domRef) {
        window.BlackHole3D.m_re_em_window_width = domRef.clientWidth
        window.BlackHole3D.m_re_em_window_height = domRef.clientHeight
      }
    },
    copyCanvas() {
      const engineContainer = this.$refs.engineContainer
      const engineCanvas = document.getElementById('engineCanvas')
      engineContainer.append(engineCanvas)

      this.setCanvasSize()
    },
    isEnginePreLoaded() {
      let isPreLoaded = false
      const engineCanvas = document.getElementById('engineCanvas')
      const classList = engineCanvas.classList
      if (classList.contains('is-loaded')) {
        isPreLoaded = true
      }
      return isPreLoaded
    },
    isEngineCanvasExist() {
      let isExist = false
      const engineCanvas = document.getElementById('engineCanvas')
      if (engineCanvas) {
        isExist = true
      } else {
        isExist = false
      }
      return isExist
    },
    createEngineCanvas() {
      const engingCanvasDom = document.createElement('canvas')
      engingCanvasDom.id = 'engineCanvas'
      engingCanvasDom.classList.add('engine-canvas-box')
      // prettier-ignore
      engingCanvasDom.oncontextmenu = e => { e.preventDefault() }
      engingCanvasDom.tabIndex = '1'

      const engineContainer = document.getElementById('engineContainer')
      engineContainer.append(engingCanvasDom)
    }
  }
}
</script>

<style lang="less" scoped>
.scene-view {
  position: relative;
  width: 100%;
  height: 100%;
  background: var(--eng-loading-bgColor);

  .engine-container {
    height: 100%;
    :deep(.engine-canvas-box) {
      outline: none;
      &:focus {
        outline: none;
      }
    }
  }
}
</style>
